import type {CouponTemplate, CouponTemplateList} from '../../types';

import {Divider, Loading} from '@ds/components';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import {useAppDispatch, useAppSelector} from 'store';
import {getCouponTemplateList} from 'store/slices/coupon/thunk';

import CouponTemplateItem from './components/CouponTemplateItem/CouponTemplateItem';

type CouponTemplateSelectProps = {
  vendorId: string;
  onSelect: (template: CouponTemplate) => void;
};

const TEMPLATE_PAGE_SIZE = 10;

const CouponTemplateSelect: React.FC<CouponTemplateSelectProps> = ({vendorId, onSelect}) => {
  const dispatch = useAppDispatch();
  const {templateList, loading} = useAppSelector((state) => state.coupon);
  const templatesData = templateList as CouponTemplateList | null;
  const isLoadingMoreRef = useRef(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const loadTemplates = useCallback(
    (page: number) =>
      dispatch(
        getCouponTemplateList({
          vendor_id: vendorId,
          page_size: TEMPLATE_PAGE_SIZE,
          page
        })
      ),
    [dispatch, vendorId]
  );

  useEffect(() => {
    void loadTemplates(1);
  }, [loadTemplates]);

  const templates = useMemo(
    () =>
      [...(templatesData?.templates ?? [])].sort((first, second) =>
        first.reward_mode.localeCompare(second.reward_mode)
      ),
    [templatesData?.templates]
  );

  const totalItems = templatesData?.total_items ?? templates.length;
  const loadMore = () => {
    if (isLoadingMoreRef.current || !templatesData || templates.length >= totalItems) {
      return;
    }

    isLoadingMoreRef.current = true;
    setIsLoadingMore(true);
    void loadTemplates(templatesData.page + 1)
      .unwrap()
      .catch(() => undefined)
      .finally(() => {
        isLoadingMoreRef.current = false;
        setIsLoadingMore(false);
      });
  };

  if (loading && templates.length === 0) {
    return (
      <div className="flex min-h-60 flex-1 items-center justify-center">
        <Loading size="large" color="primary" />
      </div>
    );
  }

  return (
    <div id="coupon-template-list" className="min-h-0 flex-1 overflow-y-auto">
      <InfiniteScroll
        next={loadMore}
        dataLength={templates.length}
        scrollableTarget="coupon-template-list"
        hasMore={templates.length < totalItems}
        loader={
          isLoadingMore ? (
            <div className="flex h-10 justify-center">
              <Loading size="medium" color="primary" />
            </div>
          ) : null
        }
        scrollThreshold={0.6}
      >
        {templates.map((template, index) => {
          const nextTemplate = templates[index + 1];
          const endsGroup = !nextTemplate || nextTemplate.reward_mode !== template.reward_mode;

          return (
            <div key={template.id}>
              <CouponTemplateItem template={template} hasBorder={!endsGroup} onSelect={onSelect} />
              {nextTemplate && endsGroup ? <Divider className="bg-gray-02" size={2} /> : null}
            </div>
          );
        })}
      </InfiniteScroll>
    </div>
  );
};

export default CouponTemplateSelect;
